import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "写真の背景を消す方法5選【PC・スマホ・無料対応】",
	description:
		"写真の背景を消す方法をPC・スマホ・無料ツールに分けて5選で解説。AIツールからPhotoshop代替まで、目的別に選べる背景除去の完全ガイド。",
	publishDate: "2026-03-24",
	slug: "photo-background-remove-methods",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function PhotoBackgroundRemoveMethods() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section>
						<p className="text-gray-700 mb-6">
							商品写真の背景を白くしたい、人物写真から背景だけ消したい——そんな場面で「どのツールを使えばいいか」迷う方は多いはずです。
							この記事では、PC・スマホ・無料ツールを組み合わせた背景除去の方法を5つに絞って解説します。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							写真の背景を消す方法 比較早見表
						</h2>
						<div className="overflow-x-auto mb-6">
							<table className="w-full text-sm border-collapse">
								<thead>
									<tr className="bg-gray-100">
										<th className="border px-3 py-2 text-left">方法</th>
										<th className="border px-3 py-2 text-left">対応デバイス</th>
										<th className="border px-3 py-2 text-left">費用</th>
										<th className="border px-3 py-2 text-left">難易度</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="border px-3 py-2">しろぬきや（AI）</td>
										<td className="border px-3 py-2">PC・スマホ</td>
										<td className="border px-3 py-2">無料〜</td>
										<td className="border px-3 py-2">★☆☆ 簡単</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border px-3 py-2">remove.bg</td>
										<td className="border px-3 py-2">PC・スマホ</td>
										<td className="border px-3 py-2">無料（月50枚）</td>
										<td className="border px-3 py-2">★☆☆ 簡単</td>
									</tr>
									<tr>
										<td className="border px-3 py-2">Canva背景リムーバー</td>
										<td className="border px-3 py-2">PC・スマホ</td>
										<td className="border px-3 py-2">無料枠あり</td>
										<td className="border px-3 py-2">★☆☆ 簡単</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border px-3 py-2">PhotoRoom（アプリ）</td>
										<td className="border px-3 py-2">スマホ中心</td>
										<td className="border px-3 py-2">無料（透かし入り）</td>
										<td className="border px-3 py-2">★☆☆ 簡単</td>
									</tr>
									<tr>
										<td className="border px-3 py-2">GIMP（手動）</td>
										<td className="border px-3 py-2">PC</td>
										<td className="border px-3 py-2">完全無料</td>
										<td className="border px-3 py-2">★★★ 難しい</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							方法1：しろぬきや — 登録不要・AI自動・無料
						</h2>
						<p className="text-gray-700 mb-4">
							しろぬきやはブラウザだけで動くAI背景除去ツールです。
							アプリのインストールもアカウント登録も不要で、画像をアップロードするだけで背景を自動で消せます。
						</p>
						<div className="bg-indigo-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ul className="space-y-2">
								<li>
									<span className="font-semibold">対応デバイス：</span>
									PC・スマホ・タブレット（ブラウザ対応）
								</li>
								<li>
									<span className="font-semibold">費用：</span>
									無料（登録不要）。Proプランは月額¥500で無制限
								</li>
								<li>
									<span className="font-semibold">特徴：</span>
									EC出品用の白背景自動変換・リサイズ機能つき
								</li>
								<li>
									<span className="font-semibold">商用利用：</span>OK
								</li>
							</ul>
						</div>
						<p className="text-gray-700 mb-4">
							Amazon・楽天・メルカリなどのEC出品に使う場合、背景除去後に自動でモール規格サイズへリサイズできる点が他ツールにない強みです。
							画像はサーバーに送信されないためプライバシーも安全です。
						</p>

						<CtaBlock />
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							方法2：remove.bg — 高精度で定番
						</h2>
						<p className="text-gray-700 mb-4">
							背景除去ツールの定番として知名度が高いサービスです。
							髪の毛など細かい部分の処理精度が高く、人物写真に特に強いとされています。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ul className="space-y-2">
								<li>
									<span className="font-semibold">無料枠：</span>
									月50枚・低解像度（約625×400px）
								</li>
								<li>
									<span className="font-semibold">有料：</span>
									月額$9〜（高解像度・商用OK）
								</li>
								<li>
									<span className="font-semibold">注意点：</span>
									無料版はAmazon出品に必要な解像度には不足
								</li>
							</ul>
						</div>
						<p className="text-gray-700">
							無料版の解像度制限（約625×400px）はEC出品用途には低すぎます。
							商用・高解像度が必要な場合は有料プランか代替ツールを検討しましょう。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							方法3：Canva背景リムーバー — デザインと一体化
						</h2>
						<p className="text-gray-700 mb-4">
							Canvaのデザインツール内に統合された背景除去機能です。
							背景を消した後にそのままデザイン編集やテキスト追加ができるため、SNS用の画像作成などに向いています。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ul className="space-y-2">
								<li>
									<span className="font-semibold">費用：</span>
									Canvaアカウント必須。無料枠あり（月一定枚数）
								</li>
								<li>
									<span className="font-semibold">強み：</span>
									背景除去後にそのままデザイン編集
								</li>
								<li>
									<span className="font-semibold">弱点：</span>
									複雑な輪郭は手動修正が必要なことも
								</li>
							</ul>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							方法4：PhotoRoom — スマホアプリで手軽に
						</h2>
						<p className="text-gray-700 mb-4">
							スマートフォン向けアプリとしても人気の高いツールです。
							商品撮影後すぐにスマホで背景を消せるため、フリマアプリやSNSへの出品に向いています。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ul className="space-y-2">
								<li>
									<span className="font-semibold">費用：</span>
									無料（透かし入り）。有料プランで透かし除去
								</li>
								<li>
									<span className="font-semibold">対応：</span>
									iOS・Android・Web版あり
								</li>
								<li>
									<span className="font-semibold">強み：</span>
									AI精度が高く、エッジ処理が丁寧
								</li>
							</ul>
						</div>
						<p className="text-gray-700">
							無料版には透かし（ウォーターマーク）が入るため、EC商用利用には有料プランが必要です。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							方法5：GIMP — 枚数無制限・完全無料（上級者向け）
						</h2>
						<p className="text-gray-700 mb-4">
							インストール型の無料画像編集ソフトです。
							「ファジー選択」や「色選択」ツールを使って背景を手動で削除します。
							操作に慣れるまで時間がかかりますが、枚数制限なし・商用OK・高解像度対応と自由度が高いのが特徴です。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ul className="space-y-2">
								<li>
									<span className="font-semibold">費用：</span>
									完全無料・枚数無制限
								</li>
								<li>
									<span className="font-semibold">強み：</span>
									細部を手動で調整できる高い自由度
								</li>
								<li>
									<span className="font-semibold">弱点：</span>
									操作習熟が必要。1枚あたりの作業時間が長い
								</li>
							</ul>
						</div>
						<p className="text-gray-700">
							大量の商品画像を素早く処理したい場合は、GIMPよりもAIツールの方が効率的です。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							目的別おすすめの選び方
						</h2>
						<div className="space-y-4 text-sm text-gray-700">
							<div className="border-l-4 border-indigo-400 pl-4">
								<p className="font-semibold mb-1">
									EC出品（Amazon・楽天・メルカリ）
								</p>
								<p>→ しろぬきや（白背景変換＋EC規格リサイズが一括対応）</p>
							</div>
							<div className="border-l-4 border-gray-400 pl-4">
								<p className="font-semibold mb-1">人物・ポートレート写真</p>
								<p>→ remove.bg または PhotoRoom（エッジ精度が高い）</p>
							</div>
							<div className="border-l-4 border-gray-400 pl-4">
								<p className="font-semibold mb-1">SNS・デザイン用</p>
								<p>→ Canva背景リムーバー（背景除去後そのままデザイン編集）</p>
							</div>
							<div className="border-l-4 border-gray-400 pl-4">
								<p className="font-semibold mb-1">コスト重視・枚数無制限</p>
								<p>
									→ GIMP（完全無料）、またはしろぬきやのProプラン（月額¥500）
								</p>
							</div>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">まとめ</h2>
						<p className="text-gray-700 mb-4">
							写真の背景を消す方法は目的によって最適なツールが変わります。
							EC出品用途であれば、背景除去からリサイズまで一括対応できるしろぬきやが最もスムーズです。
							登録不要・無料で今すぐ試せます。
						</p>

						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
